import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Homepage Expression of Interest form — obsidian/gold, credential-style card.
 * Creates an incomplete account and emails a magic link into the master form.
 */
export const ExpressInterestForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", role_interest: "buyer", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [magicUrl, setMagicUrl] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/eoi/submit`, form);
      setMagicUrl(r.data?.magic_url || "");
      setSent(true);
      toast.success("Registration file opened");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — try again");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <section id="eoi" className="relative py-24 md:py-36 border-t border-white/10" data-testid="eoi-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="border border-[#B87333]/40 rounded-sm p-12 bg-white/[0.02]"
            style={{ boxShadow: "inset 0 0 100px rgba(184,115,51,0.14)" }}>
            <ShieldCheck className="w-12 h-12 mx-auto text-[#2FB999] mb-6" />
            <div className="overline text-[#B87333] mb-3">Your File is Open</div>
            <h2 className="font-serif text-[clamp(2rem,4vw,3.5rem)] leading-[1.1]">Continue to registration</h2>
            <p className="font-body text-[#8A8A82] mt-4 max-w-lg mx-auto">
              Your Vinci registration file for <b className="text-[#F5F5F0]">{form.email}</b> is open. Tap below to jump straight into the master form — if an email arrives in your inbox, you can also open it from there.
            </p>
            {magicUrl && (
              <a href={magicUrl} data-testid="eoi-continue-cta"
                className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#B87333] text-black px-8 py-3 text-xs uppercase tracking-[0.28em] font-grotesk hover:brightness-110">
                Open my registration file →
              </a>
            )}
            <p className="font-body text-[10px] text-[#8A8A82] mt-6 uppercase tracking-[0.24em]">
              Link valid for 24 hours
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="eoi" className="relative py-24 md:py-36 border-t border-white/10" data-testid="eoi-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="overline text-[#B87333]">/ Vinci Registry · Join</span>
          <h2 className="font-serif text-[clamp(2.2rem,5vw,5rem)] leading-[1] tracking-[-0.02em] mt-6">
            Express your <span className="italic text-[#B87333]">interest.</span>
          </h2>
          <p className="font-body text-[#8A8A82] leading-relaxed mt-8 max-w-md">
            Every partner on the Vinci network is registered, verified and issued an 8-digit credential in the 5151 series.
            Start your file today — the trade desk mediates every quotation, order and settlement.
          </p>
          <div className="mt-10 flex items-center gap-3 text-xs text-[#8A8A82]">
            <ShieldCheck className="w-4 h-4 text-[#2FB999]" />
            <span>Passwordless start · magic-link in your inbox</span>
          </div>
        </div>

        <form onSubmit={submit} data-testid="eoi-form"
          className="lg:col-span-7 border border-[#B87333]/40 rounded-sm p-8 md:p-10 bg-[#0a0806]"
          style={{ boxShadow: "inset 0 0 90px rgba(184,115,51,0.10)" }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <EField label="Full Name *" testid="eoi-name" value={form.name} onChange={(v) => set("name", v)} />
            <EField label="Company" testid="eoi-company" value={form.company} onChange={(v) => set("company", v)} />
            <EField label="Email *" testid="eoi-email" type="email" value={form.email} onChange={(v) => set("email", v)} />
            <EField label="Phone *" testid="eoi-phone" value={form.phone} onChange={(v) => set("phone", v)} />
          </div>
          <div className="mt-6">
            <div className="overline text-[#8A8A82] mb-3">I want to join as</div>
            <div className="grid grid-cols-3 gap-2">
              {[["buyer", "Buyer"], ["seller", "Seller"], ["custodian", "Custodian"]].map(([k, l]) => (
                <button key={k} type="button" data-testid={`eoi-role-${k}`} onClick={() => set("role_interest", k)}
                  className={`rounded-sm border px-4 py-3 text-xs uppercase tracking-[0.22em] font-grotesk transition-colors ${form.role_interest === k ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10" : "border-white/15 text-[#C9C9C2] hover:border-white/30"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <label className="block mt-6">
            <div className="overline text-[#8A8A82] mb-2">Message (optional)</div>
            <textarea data-testid="eoi-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-2 font-body text-sm text-[#F5F5F0] resize-none" />
          </label>
          <button data-testid="eoi-submit" type="submit" disabled={busy}
            className="mt-8 w-full rounded-full bg-[#B87333] text-black px-6 py-3 text-xs uppercase tracking-[0.28em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Open my registration file
          </button>
        </form>
      </div>
    </section>
  );
};

function EField({ label, testid, value, onChange, type = "text" }) {
  return <label className="block group">
    <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">{label}</span>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] transition-colors" />
  </label>;
}
